import { useEffect, useState } from "react";
import { X, Save, Mail } from "lucide-react";
import { adminFetch, money, when, ADMIN_STATUSES, CARRIERS, STATUS_LABELS } from "../../lib/adminApi";
import { StatusBadge } from "./OrdersTable";

const Row = ({ label, children, testId }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm"><span className="text-gray-500">{label}</span><span className="text-gray-200 text-right" data-testid={testId}>{children}</span></div>
);
const inputCls = "w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 text-white text-sm focus:border-signal-red focus:outline-none";

export const OrderDetail = ({ order, onClose, onSaved }) => {
  const [form, setForm] = useState({ status: order.status, tracking_carrier: order.tracking_carrier || "", tracking_number: order.tracking_number || "", internal_notes: order.internal_notes || "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { setForm({ status: order.status, tracking_carrier: order.tracking_carrier || "", tracking_number: order.tracking_number || "", internal_notes: order.internal_notes || "" }); setMsg(null); }, [order]);

  const a = order.shipping_address, c = order.customer;
  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const body = {};
      if (form.status !== order.status) body.status = form.status;
      if (form.tracking_carrier !== (order.tracking_carrier || "")) body.tracking_carrier = form.tracking_carrier;
      if (form.tracking_number !== (order.tracking_number || "")) body.tracking_number = form.tracking_number;
      if (form.internal_notes !== (order.internal_notes || "")) body.internal_notes = form.internal_notes;
      if (Object.keys(body).length === 0) { setMsg({ ok: true, text: "No changes to save." }); return; }
      const updated = await adminFetch(`/api/orders/${order.order_id}`, { method: "PATCH", body });
      onSaved(updated);
      setMsg({ ok: true, text: "Saved." });
    } catch (e) { setMsg({ ok: false, text: e.message }); } finally { setBusy(false); }
  };

  const resendShipping = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await adminFetch(`/api/orders/${order.order_id}/shipping-email`, { method: "POST" });
      setMsg({ ok: true, text: r.email_enabled ? "Shipping confirmation queued." : "Queued — email sending is disabled until the Resend domain is verified (EMAIL_ENABLED=false)." });
    } catch (e) { setMsg({ ok: false, text: e.message }); } finally { setBusy(false); }
  };

  return (
    <aside className="bg-[#141414] border border-white/10 p-6 h-fit lg:sticky lg:top-24 max-h-[calc(100vh-7rem)] overflow-y-auto" data-testid="order-detail">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500">Order</p>
          <h2 className="font-heading text-2xl font-black text-white" data-testid="order-detail-number">{order.order_number}</h2>
          <p className="text-gray-500 text-xs">{when(order.created_at)} · {order.stripe_mode === "test" ? "STRIPE TEST MODE" : "live"}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-white" data-testid="order-detail-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex gap-2 mb-5"><StatusBadge status={order.status} testId="order-detail-status" /><span className="px-2 py-0.5 border border-white/20 text-[11px] font-mono uppercase text-gray-300" data-testid="order-detail-payment">Stripe: {order.payment_status}</span></div>

      <section className="border-t border-white/10 pt-4 mb-4">
        <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-signal-red mb-2">Customer</h3>
        <Row label="Name" testId="order-detail-customer">{c.first_name} {c.last_name}</Row>
        <Row label="Company">{c.company || "—"}</Row>
        <Row label="Email"><a href={`mailto:${c.email}`} className="hover:text-signal-red">{c.email}</a></Row>
        <Row label="Phone"><a href={`tel:${c.phone}`} className="hover:text-signal-red">{c.phone}</a></Row>
        <Row label="Lift type (optional)" testId="order-detail-lift-type">{c.lift_type || "Not provided"}</Row>
        <Row label="Ships to">{a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />{a.city}, {a.state} {a.postal_code} {a.country}{a.residential ? " (residential)" : ""}</Row>
      </section>

      <section className="border-t border-white/10 pt-4 mb-4">
        <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-signal-red mb-2">Items</h3>
        <table className="w-full text-sm" data-testid="order-detail-items">
          <thead><tr className="text-gray-500 text-xs text-left"><th className="py-1">Product</th><th>SKU</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Line</th></tr></thead>
          <tbody>{order.items.map((l) => (
            <tr key={l.sku} className="text-gray-200"><td className="py-1">{l.name} <span className="text-gray-500">({l.model})</span></td><td className="font-mono text-xs">{l.sku}</td><td className="text-right">{l.quantity}</td><td className="text-right">{money(l.unit_price_cents)}</td><td className="text-right font-bold">{money(l.line_total_cents)}</td></tr>
          ))}</tbody>
        </table>
        <div className="mt-3 pt-3 border-t border-white/10">
          <Row label="Subtotal">{money(order.subtotal_cents)}</Row>
          <Row label={`Shipping · ${order.shipping_service || "—"}`}>{money(order.shipping_cents)}</Row>
          <Row label="Tax (Stripe Tax)">{money(order.tax_cents)}</Row>
          <Row label="Total" testId="order-detail-total"><strong className="text-white">{money(order.total_cents)}</strong></Row>
        </div>
      </section>

      <section className="border-t border-white/10 pt-4 mb-4 space-y-3">
        <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-signal-red">Fulfillment</h3>
        <p className="text-gray-500 text-xs">Fulfillment status is internal. It never changes Stripe payment status; refunds are issued in Stripe only.</p>
        <label className="block text-xs text-gray-400">Status
          <select name="status" value={form.status} onChange={set} className={inputCls} data-testid="order-status-select">
            {order.status === "pending_payment" && <option value="pending_payment">Pending Payment</option>}
            {ADMIN_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs text-gray-400">Carrier
            <select name="tracking_carrier" value={form.tracking_carrier} onChange={set} className={inputCls} data-testid="order-carrier-select">
              <option value="">—</option>{CARRIERS.map((x) => <option key={x}>{x}</option>)}
            </select>
          </label>
          <label className="block text-xs text-gray-400">Tracking number
            <input name="tracking_number" value={form.tracking_number} onChange={set} className={inputCls} data-testid="order-tracking-input" />
          </label>
        </div>
        <label className="block text-xs text-gray-400">Internal notes
          <textarea name="internal_notes" rows={3} value={form.internal_notes} onChange={set} className={inputCls} data-testid="order-notes-input" />
        </label>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 bg-signal-red text-white font-bold uppercase text-xs tracking-wide hover:bg-signal-red/90 disabled:opacity-40" data-testid="order-save-button"><Save className="w-4 h-4" /> Save</button>
          {order.status === "shipped" && order.tracking_number && (
            <button type="button" onClick={resendShipping} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-gray-200 font-bold uppercase text-xs tracking-wide hover:border-signal-red disabled:opacity-40" data-testid="order-resend-shipping-email"><Mail className="w-4 h-4" /> Send shipping email</button>
          )}
        </div>
        {msg && <p className={`text-sm ${msg.ok ? "text-emerald-300" : "text-signal-red"}`} data-testid="order-save-message">{msg.text}</p>}
        <Row label="Shipping email">{order.shipping_email_status ? `${order.shipping_email_status} · ${when(order.shipping_email_at)}` : "not sent"}</Row>
        <Row label="Confirmation email">{order.confirmation_email_status || "not sent"}</Row>
      </section>

      <section className="border-t border-white/10 pt-4">
        <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-signal-red mb-2">History</h3>
        <ul className="text-xs text-gray-400 space-y-1" data-testid="order-history">
          {(order.status_history || []).slice().reverse().map((h, i) => <li key={i}>{when(h.at)} — <span className="text-gray-200">{STATUS_LABELS[h.status] || h.status}</span> by {h.by}{h.note ? ` (${h.note})` : ""}</li>)}
        </ul>
        <p className="text-gray-600 text-[11px] mt-3 font-mono break-all">Stripe session {order.stripe_session_id}<br />PI {order.stripe_payment_intent_id || "—"}</p>
      </section>
    </aside>
  );
};
